import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import i18n from '@/lang';
import {listMenu} from "@/api/menu";
import { translateMenuTitle } from '@/utils/i18n'

Vue.use(VueRouter);

const router = new VueRouter({
    mode: 'hash', // 使用 hash 模式
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        }
        return { x: 0, y: 0 }
    },
    routes: [
        {
            path: '/',
            name: 'LandingPage',
            component: () => import('@/views/landing/index.vue')
        },
        {
            path: '/app',
            name: 'mainLayout',
            component: () => import('@/views/layout/MainLayout.vue'),
            redirect: '/home',
            children: [
                {
                    path: '/home',
                    name: 'AnalyticsDashboard',
                    component: () => import('@/views/AnalyticsDashboard.vue')
                },
                {
                    path: '/fixdata',
                    name: 'FixDataIndex',
                    component: () => import('@/views/fixdata/index.vue'),
                    meta: {
                        titles: ['补充数据中心']
                    }
                },
                {
                    path: '/profile',
                    name: 'Profile',
                    component: () => import('@/views/profile/ProfileView.vue'),
                    meta: {
                        titles: ['个人中心']
                    }
                },
                {
                    // 分配用户原来是个顶级路由，和 /login、/pricing 平级，
                    // 所以打开之后整页没有侧边栏、没有头部，也就"连退回都没有"。
                    // 挪进 mainLayout 的 children，它才是系统配置里的一个页面 呱呱~
                    path: "/system/role-auth/user/:roleId",
                    name: 'authUser',
                    component: () => import('@/views/system/role/authUser.vue'),
                    meta: {
                        titles: ['系统配置', '角色管理', '分配用户']
                    }
                },
                {
                    // 组织与成员：后端菜单还没配，先挂静态路由，方便本地预览（数据目前是 mock）
                    path: "/organization/members",
                    name: 'OrganManager',
                    component: () => import('@/views/organManager/index.vue'),
                    meta: {
                        titles: ['配置', '组织与成员']
                    }
                }
            ]
        },
        {
            path: "/design/:projectId",
            name: "Design",
            component: () => import('@/views/design/index.vue'),
            props: true, // 允许通过路由参数传递 props
        },
        {
            // AI 问卷生成是整屏沉浸式工作台，和设计器一样放顶层（不走 MainLayout 的侧边栏）。
            // 生成过程中要占满视野，套在后台布局里会显得很挤。
            path: "/ai-builder",
            name: "AiBuilder",
            component: () => import('@/views/aiBuilder/index.vue')
        },
        {
            path: "/olddata/detail",
            name: "OldDataDetail",
            component: () => import('@/views/olddata/components/ProjectDetail.vue')
        },
        {
            path: "/project/config",
            name: "ProjectConfig",
            component: () => import('@/views/project/components/ConfigView.vue'),
            meta: {
                titles: ['项目配置']
            }
        },
        {
            path: "/project/link-config",
            name: "LinkConfig",
            component: () => import('@/views/project/components/LinkConfigView.vue'),
            meta: {
                titles: ['链接配置']
            }
        },
        {
            path: "/project/quota/schemes",
            name: "QuotaSchemeList",
            component: () => import('@/views/project/components/QuotaSchemeList.vue'),
            meta: {
                titles: ['配额方案管理']
            }
        },
        {
            path: "/project/quota/config",
            name: "QuotaConfig",
            component: () => import('@/views/project/components/QuotaConfig.vue'),
            meta: {
                titles: ['配额设置']
            }
        },
        {
            path: "/project/quota-monitor",
            name: "QuotaMonitor",
            component: () => import('@/views/project/components/QuotaMonitor.vue'),
            meta: {
                titles: ['配额监控']
            }
        },
        {
            path: "/project/report",
            name: "ProjectReport",
            component: () => import('@/views/report/CrossTab.vue'),
            meta: {
                titles: ['报表']
            }
        },
        {
            path: '/login',
            name: 'LoginPage',
            component: () => import('@/views/login/index.vue')
        },
        {
            path: '/pricing',
            name: 'PricingPage',
            component: () => import('@/views/pricing/index.vue')
        },
        {
            path: '*',
            component: () => import('@/views/404.vue'),
        }
    ]
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
    let token = localStorage.getItem("token");
    const publicPaths = ['/', '/login', '/pricing'];

    // 如果用户访问登录页面，但是token已经存在，应该跳转到首页
    if (to.path === '/login' && token) {
        next({ path: '/home' })
        return
    }

    // 如果用户访问不是登录页面和收费页面，但是没有token，跳转到登录页面
    if (!publicPaths.includes(to.path) && !token) {
        next({ path: '/login' })
        return
    }

    if (publicPaths.includes(to.path)) {
        next()
        return
    }

    // 获取用户路由菜单数据
    if (token && store.state.userMenuData.menuData.length === 0) {
        try {
            let res = await listMenu({});
            if (!res) {
                next()
                return
            }

            let newUserMenuData = [
                {
                    title: i18n.t('navbar.dashboard'),
                    path: "/home",
                    icon: 'home',
                },
            ]
            let ret = res.data.map(item => {
                return {
                    title: translateMenuTitle(item.name),
                    icon: item.icon,
                    path: "/" + item.path,
                    children: !item.children || item.children.map(sitem => {
                        return {
                            title: translateMenuTitle(sitem.name),
                            path: "/" + item.path + "/" + sitem.path,
                            icon: sitem.icon || 'setting', // 使用子菜单的icon，如果没有则使用默认icon
                        }
                    }),
                }
            })

            newUserMenuData = [...newUserMenuData, ...ret]
            store.commit("userMenuData/changeMenuData", newUserMenuData)

            //生成路由数据开始
            let newChildrenRoutes = []

            res.data.forEach(item => {
                // 项目管理总览是沉浸式全屏页：注册为顶层路由，不挂 mainLayout（天然无侧边栏/头部）
                if (item.component === 'projectManager/index') {
                    router.addRoute({
                        path: "/" + item.path,
                        component: () => import('@/views/projectManager/index.vue'),
                        meta: {
                            titles: [item.name]
                        }
                    });
                    return;
                }

                // 如果父级菜单有 component，则为其生成路由
                if (item.component) {
                    newChildrenRoutes.push({
                        path: "/" + item.path,
                        component: () => import(`../views/${item.component}.vue`),
                        meta: {
                            titles: [item.name]
                        }
                    });
                }

                // 处理子菜单项
                if (item.children) {
                    let ret = item.children.map(citem => {
                        return {
                            path: "/" + item.path + "/" + citem.path,
                            component: () => import(`../views/${citem.component}.vue`),
                            meta: {
                                titles: [item.name, citem.name]
                            }
                        };
                    });
                    newChildrenRoutes = [...newChildrenRoutes, ...ret];
                }
            });

            // 要把这个生成的数组添加到mainLayout路由的children里面
            newChildrenRoutes.forEach(item => {
                router.addRoute('mainLayout', item)
            })

            // 修改这里的重定向逻辑
            if (to.path === '/login') {
                next({ path: '/home' })
            } else {
                next({ ...to, replace: true })
            }
            return
        } catch (error) {
            console.error('路由加载失败:', error)
            next()
            return
        }
    }

    next();
})

export default router;
